import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Box,
    Paper,
    Grid,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    FormHelperText,
    FormControlLabel,
    Switch,
    Divider,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { Save, ContentCopy, VpnKey, SmartToy } from "@mui/icons-material";
import useNotification from "../../common/Notification";
import { WB_SX } from "../../../ui/ux/uiTokens";
import { ASSIGNABLE_ROLES } from "../adminUtils";
import { formatRoleLabel, parseUserRoles } from "../../UserManagement/roleLabels";
import {
    updateUserProfile,
    updateUserRoles,
    updateAccountStatus,
    getBotAutomationVersion,
    updateBotAutomationVersion,
    getApiKey,
    updateApiKey,
    listAwsSesAccounts,
} from "../../../api/superAdminApi";

const AdminUserProfilePage = () => {
    const { user, reloadUser } = useOutletContext();
    const notify = useNotification();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [profile, setProfile] = useState({ name: "", email: "", password: "", user_role: [], aws_ses_account_id: "" });
    const [accountDisabled, setAccountDisabled] = useState(false);
    const [sesAccounts, setSesAccounts] = useState([]);
    const [botVersion, setBotVersion] = useState({ bot_automation_version: "v1", allowed_versions: ["v1", "v2"] });
    const [apiKeyData, setApiKeyData] = useState({ api_key: "", has_api_key: false });
    const [customApiKey, setCustomApiKey] = useState("");

    useEffect(() => {
        if (!user) return;
        setProfile({
            name: user.name || "",
            email: user.email || "",
            password: "",
            user_role: parseUserRoles(user.user_role),
            aws_ses_account_id: user.aws_ses_account_id != null ? String(user.aws_ses_account_id) : "",
        });
        setAccountDisabled(!!user.account_disabled);
        getBotAutomationVersion(user.id).then(setBotVersion).catch(() => {});
        getApiKey(user.id).then(setApiKeyData).catch(() => {});
        listAwsSesAccounts({ status: "ACTIVE", per_page: 100 })
            .then(({ accounts }) => setSesAccounts(accounts || []))
            .catch(() => setSesAccounts([]));
    }, [user]);

    const handleSave = async () => {
        setSaving(true);
        setError("");
        try {
            const profilePayload = {};
            if (profile.name.trim() !== user.name) profilePayload.name = profile.name.trim();
            if (profile.email.trim() !== user.email) profilePayload.email = profile.email.trim();
            if (profile.password) profilePayload.password = profile.password;
            const currentSesId = user.aws_ses_account_id != null ? String(user.aws_ses_account_id) : "";
            if (profile.aws_ses_account_id !== currentSesId) {
                profilePayload.aws_ses_account_id = profile.aws_ses_account_id
                    ? Number(profile.aws_ses_account_id)
                    : null;
            }
            if (Object.keys(profilePayload).length > 0) await updateUserProfile(user.id, profilePayload);

            const roleString = profile.user_role.join(",");
            const originalRoleString = parseUserRoles(user.user_role).join(",");
            if (roleString !== originalRoleString) {
                await updateUserRoles(user.id, profile.user_role, "replace");
            }
            if (accountDisabled !== !!user.account_disabled) {
                await updateAccountStatus(user.id, accountDisabled);
            }
            notify("Profile saved", "success");
            reloadUser();
        } catch (err) {
            setError(err.message);
            notify(err.message, "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Paper elevation={0} sx={{ ...WB_SX.card, p: 3 }}>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Account details</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Name" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Email" type="email" value={profile.email} onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="New password" type="password" value={profile.password} onChange={(e) => setProfile((p) => ({ ...p, password: e.target.value }))} helperText="Leave blank to keep current" />
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Roles</InputLabel>
                        <Select
                            multiple
                            value={profile.user_role}
                            onChange={(e) => setProfile((p) => ({ ...p, user_role: typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value }))}
                            input={<OutlinedInput label="Roles" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((v) => <Chip key={v} label={formatRoleLabel(v)} size="small" />)}
                                </Box>
                            )}
                        >
                            {ASSIGNABLE_ROLES.map(({ value, group }) => (
                                <MenuItem key={value} value={value}>{formatRoleLabel(value)} ({group})</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>Replaces all roles when saved</FormHelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel control={<Switch checked={accountDisabled} onChange={(e) => setAccountDisabled(e.target.checked)} color="error" />} label="Account disabled (blocks login)" />
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                        <InputLabel>AWS SES account</InputLabel>
                        <Select
                            label="AWS SES account"
                            value={profile.aws_ses_account_id}
                            onChange={(e) => setProfile((p) => ({ ...p, aws_ses_account_id: e.target.value }))}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {sesAccounts.map((account) => (
                                <MenuItem key={account.id} value={String(account.id)}>
                                    {account.name} ({account.aws_region})
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            Required before this user can create email domains (especially for EMAIL-ADMIN).
                        </FormHelperText>
                    </FormControl>
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <VpnKey fontSize="small" /> API Key
            </Typography>
            {apiKeyData.has_api_key ? (
                <TextField
                    fullWidth
                    size="small"
                    label="Current API key"
                    value={apiKeyData.api_key || "••••••••"}
                    sx={{ mb: 1 }}
                    InputProps={{
                        readOnly: true,
                        endAdornment: apiKeyData.api_key ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => { navigator.clipboard.writeText(apiKeyData.api_key); notify("Copied", "success"); }}>
                                    <ContentCopy fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No API key set</Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                <Button size="small" variant="outlined" disabled={saving} onClick={async () => {
                    setSaving(true);
                    try { await updateApiKey(user.id, { generate: true }); setApiKeyData(await getApiKey(user.id)); notify("Generated", "success"); }
                    catch (e) { notify(e.message, "error"); } finally { setSaving(false); }
                }}>Generate</Button>
                <TextField size="small" placeholder="Custom key" value={customApiKey} onChange={(e) => setCustomApiKey(e.target.value)} sx={{ minWidth: 160 }} />
                <Button size="small" variant="outlined" disabled={saving || !customApiKey} onClick={async () => {
                    setSaving(true);
                    try { await updateApiKey(user.id, { api_key: customApiKey }); setApiKeyData(await getApiKey(user.id)); setCustomApiKey(""); notify("Set", "success"); }
                    catch (e) { notify(e.message, "error"); } finally { setSaving(false); }
                }}>Set</Button>
                {apiKeyData.has_api_key && (
                    <Button size="small" color="error" disabled={saving} onClick={async () => {
                        setSaving(true);
                        try { await updateApiKey(user.id, { clear: true }); setApiKeyData({ api_key: "", has_api_key: false }); notify("Cleared", "success"); }
                        catch (e) { notify(e.message, "error"); } finally { setSaving(false); }
                    }}>Clear</Button>
                )}
            </Box>

            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <SmartToy fontSize="small" /> Bot automation version
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 3, maxWidth: 240 }}>
                <InputLabel>Version</InputLabel>
                <Select
                    label="Version"
                    value={botVersion.bot_automation_version || "v1"}
                    onChange={async (e) => {
                        setSaving(true);
                        try {
                            await updateBotAutomationVersion(user.id, e.target.value);
                            setBotVersion((p) => ({ ...p, bot_automation_version: e.target.value }));
                            notify("Bot version updated", "success");
                        } catch (err) { notify(err.message, "error"); } finally { setSaving(false); }
                    }}
                >
                    {(botVersion.allowed_versions || ["v1", "v2"]).map((v) => (
                        <MenuItem key={v} value={v}>{v}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={handleSave} disabled={saving} sx={WB_SX.primaryButton}>
                Save profile
            </Button>
        </Paper>
    );
};

export default AdminUserProfilePage;
